import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { fetchAfPrices, getErrorMessage, isNotFound } from '@/api';
import { useMetaContext } from '@/components/Layout';
import { Btn, Card, OSBadges, SkeletonText } from '@/components/ui';
import { useProject, useProjectMembers } from '@/hooks';
import { projectLabel } from '@/lib/format';

import { afAppIdFromStoreId } from './prices/afCpeShared';
import InfoTab from './projectDetail/InfoTab';
import PermTab from './projectDetail/PermTab';
import ProjectIconEditor from './projectDetail/ProjectIconEditor';
import styles from './ProjectDetail.module.css';

const AfCpePricesPage = lazy(() => import('./prices/AfCpePrices'));

type TabKey = 'info' | 'perm' | 'af-cpe';
type PriceOs = 'aos' | 'ios';

export default function ProjectDetailPage() {
  const { project_id: projectId } = useParams<{ project_id: string }>();
  const meta = useMetaContext();
  const [tab, setTab] = useState<TabKey>('info');
  const [afPriceCount, setAfPriceCount] = useState(0);
  const [afPriceCountLoaded, setAfPriceCountLoaded] = useState(false);
  const [afPriceCountRefreshKey, setAfPriceCountRefreshKey] = useState(0);

  const project = useProject(projectId);
  const members = useProjectMembers(projectId);
  // 커스텀 역할은 메뉴 매핑으로만 권한을 받는다 — 단가 관리는 admin·leader·member 보유자만(서버 prices._actor_email 과 동일).
  const canManageAfPrices = meta.current_user.roles.some((r) => r === 'admin' || r === 'leader' || r === 'member');
  const afPriceApps = useMemo(() => {
    if (!project.data) return [];
    return project.data.platforms.flatMap((platform) => {
      const os = platform.code.toLowerCase();
      if (os !== 'aos' && os !== 'ios') return [];
      const appId = afAppIdFromStoreId(platform.store_id, os as PriceOs);
      if (!appId) return [];
      return [{ appId, os: os as PriceOs }];
    });
  }, [project.data]);

  useEffect(() => {
    if (tab !== 'af-cpe' || afPriceCountLoaded) return undefined;
    if (!canManageAfPrices || afPriceApps.length === 0) {
      setAfPriceCount(0);
      setAfPriceCountLoaded(true);
      return undefined;
    }
    const ctrl = new AbortController();
    Promise.all(afPriceApps.map(({ appId, os }) => (
      fetchAfPrices({ app_id: appId, os }, ctrl.signal)
    )))
      .then((results) => setAfPriceCount(
        results.reduce((total, result) => total + result.prices.length, 0),
      ))
      .then(() => setAfPriceCountLoaded(true))
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setAfPriceCount(0);
        setAfPriceCountLoaded(true);
      });
    return () => ctrl.abort();
  }, [afPriceApps, afPriceCountLoaded, afPriceCountRefreshKey, canManageAfPrices, tab]);

  // 첫 load — skeleton 헤더+탭+컨텐츠 (ProjectList의 카드 skeleton과 동일 톤).
  // reload 시점엔 project.data가 useFetch에 의해 유지되어 stale-while-revalidate로 동작.
  if (project.loading && !project.data) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={`${styles.thumb} ${styles.skel} ${styles.skelThumb}`} aria-hidden="true" />
          <div className={styles.skelHeaderInfo} aria-hidden="true">
            <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '40%', height: 22 }} />
            <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '70%', height: 12 }} />
            <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '55%', height: 12 }} />
          </div>
        </div>
        <div className={styles.skelTabs} aria-hidden="true">
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: 96, height: 32, borderRadius: 8 }} />
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: 96, height: 32, borderRadius: 8 }} />
        </div>
        <div className={styles.skelContent} aria-hidden="true">
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '30%', height: 14 }} />
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '85%', height: 12 }} />
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '70%', height: 12 }} />
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '90%', height: 12 }} />
          <div className={`${styles.skel} ${styles.skelLine}`} style={{ width: '60%', height: 12 }} />
        </div>
      </div>
    );
  }

  if (project.error || !project.data) {
    const message = project.error ? getErrorMessage(project.error) : '데이터 없음';
    const notFound = project.error ? isNotFound(project.error) : false;
    return (
      <div className={styles.page}>
        <Card>
          <div className={styles.notFound}>
            <div className={styles.notFoundTitle}>
              {notFound ? '프로젝트를 찾을 수 없어요' : '프로젝트를 불러오지 못했어요'}
            </div>
            <div style={{ color: 'var(--sc-text-muted)', marginBottom: 16 }}>{message}</div>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <Btn variant="primary">목록으로</Btn>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const p = project.data;
  const memberCount = members.data?.members.length ?? p.member_count;

  // stale-while-revalidate: data가 있을 때 reload 진행 중이면 약간 dim.
  const dimClass = project.loading && project.data ? styles.staleDim : '';

  return (
    <div className={`${styles.page} ${dimClass}`}>
      {/* 헤더 */}
      <div className={styles.header}>
        <ProjectIconEditor
          projectId={p.project_id ?? null}
          prefix={p.prefix}
          displayName={p.display_name}
          iconUrl={p.icon_url ?? null}
          size={88}
          className={styles.thumb}
          onUploaded={project.reload}
        />
        <div className={styles.headerInfo}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{projectLabel(p.display_name, p.prefix)}</h1>
            <OSBadges os={p.os || []} />
          </div>
          <div className={styles.metaRow}>
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.prefix}</span>
            <span className={styles.metaDot} />
            <span style={{ whiteSpace: 'nowrap' }}>
              project_id <span style={{ color: 'var(--sc-text)', fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.project_id ?? '—'}</span>
            </span>
            {p.gcp_project_id && (
              <>
                <span className={styles.metaDot} />
                <span style={{ whiteSpace: 'nowrap' }}>
                  gcp_project_id <span style={{ color: 'var(--sc-text)', fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.gcp_project_id}</span>
                </span>
              </>
            )}
            {p.updated_at && (
              <>
                <span className={styles.metaDot} />
                <span style={{ whiteSpace: 'nowrap' }}>최근 수정 {p.updated_at}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 탭 */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'info' ? styles.active : ''}`}
          onClick={() => setTab('info')}
        >
          정보 수정
          <span className={styles.tabCount}>6</span>
        </button>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'perm' ? styles.active : ''}`}
          onClick={() => setTab('perm')}
        >
          권한 관리
          <span className={styles.tabCount}>{memberCount}</span>
        </button>
        {canManageAfPrices && (
          <button
            type="button"
            className={`${styles.tab} ${tab === 'af-cpe' ? styles.active : ''}`}
            onClick={() => setTab('af-cpe')}
          >
            단가 관리
            {afPriceCountLoaded && <span className={styles.tabCount}>{afPriceCount}</span>}
          </button>
        )}
      </div>

      {tab === 'info' ? (
        <InfoTab
          project={p}
          onSaved={project.reload}
          reloading={project.loading && !!project.data}
        />
      ) : tab === 'perm' ? (
        <PermTab projectId={p.project_id} prefix={p.prefix} membersState={members} />
      ) : (
        <Suspense fallback={<Card><SkeletonText lines={5} lastWidth="45%" /></Card>}>
          <AfCpePricesPage
            project={p}
            onPricesChanged={() => {
              setAfPriceCountLoaded(false);
              setAfPriceCountRefreshKey((current) => current + 1);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}
